import React, { useState, useEffect, useCallback } from 'react';
import { 
  Card, 
  Typography, 
  Table, 
  Button, 
  Space, 
  Tag, 
  Modal, 
  Form, 
  Input, 
  message,
  Popconfirm,
  Tooltip,
  Select,
  Row,
  Col
} from 'antd';
import { 
  UserOutlined, 
  PlusOutlined, 
  EditOutlined, 
  DeleteOutlined,
  SearchOutlined,
  KeyOutlined,
  MailOutlined,
  CrownOutlined,
  TeamOutlined
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import { User, UserRole } from '../../types';
import { formatLocalizedDateTime } from '../../utils/dateUtils';
import { userService, SearchUsersDto } from '../../services/userService';
import { validationRules } from '../../utils/formUtils';
import { showError, showLoadError, showSaveError, showDeleteError, showStatusChangeError } from '../common/ErrorMessage';

const { Title } = Typography;
const { Option } = Select;

const UserManagement: React.FC = () => {
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [passwordModalVisible, setPasswordModalVisible] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [searchForm] = Form.useForm();
  const [form] = Form.useForm();
  const [passwordForm] = Form.useForm();
  const { user: currentUser } = useAuth();
  const { t, i18n } = useTranslation();

  const fetchUsers = useCallback(async (searchParams?: SearchUsersDto) => {
    setLoading(true);
    try {
      const data = searchParams 
        ? await userService.searchUsers(searchParams)
        : await userService.getAllUsers();
      
      setUsers(data);
    } catch (error: any) {
      showLoadError(t('userManagement.list'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const handleSearch = async (values: SearchUsersDto) => {
    try {
      const data = await userService.searchUsers(values);
      setUsers(data);
    } catch (error: any) {
      showError(error, t('userManagement.searchFailed'));
    }
  };

  const handleCreate = () => {
    setEditingUser(null);
    form.resetFields();
    setModalVisible(true);
  };

  const handleEdit = (user: User) => {
    setEditingUser(user);
    form.setFieldsValue({
      username: user.username,
      email: user.email,
      role: user.role,
      organ_name: user.organ_name || '',
      bubmyeng: user.bubmyeng || '',
    });
    setModalVisible(true);
  };

  const handleSave = async (values: any) => {
    try {
      if (editingUser) {
        await userService.updateUser(editingUser.user_id, values);
        message.success(t('userManagement.updateSuccess'));
      } else {
        await userService.createUser(values);
        message.success(t('userManagement.createSuccess'));
      }
      
      setModalVisible(false);
      setEditingUser(null);
      form.resetFields();
      fetchUsers();
    } catch (error: any) {
      showSaveError(t('userManagement.list'));
    }
  };

  const handleDelete = async (userId: string) => {
    try {
      await userService.deleteUser(userId);
      message.success(t('userManagement.deleteSuccess'));
      fetchUsers();
    } catch (error: any) {
      showDeleteError(t('userManagement.list'));
    }
  };

  const handlePasswordChange = (user: User) => {
    setSelectedUser(user);
    passwordForm.resetFields();
    setPasswordModalVisible(true);
  };

  const handlePasswordSave = async (values: any) => {
    try {
      if (selectedUser) {
        await userService.updateUserPassword(selectedUser.user_id, values.password);
        message.success(t('userManagement.passwordChangeSuccess'));
        setPasswordModalVisible(false);
        setSelectedUser(null);
        passwordForm.resetFields();
      }
    } catch (error: any) {
      showStatusChangeError(error, t('userManagement.modal.password'));
    }
  };

  const getRoleColor = (role: UserRole) => {
    switch (role) {
      case UserRole.ADMIN:
        return 'red';
      case UserRole.GENERAL:
        return 'blue';
      default:
        return 'default';
    }
  };

  const getRoleText = (role: UserRole) => {
    switch (role) {
      case UserRole.ADMIN:
        return t('userManagement.roles.admin');
      case UserRole.GENERAL:
        return t('userManagement.roles.general');
      default:
        return t('userManagement.roles.unknown');
    }
  };

  const getRoleIcon = (role: UserRole) => {
    switch (role) {
      case UserRole.ADMIN:
        return <CrownOutlined />;
      case UserRole.GENERAL:
        return <TeamOutlined />;
      default:
        return <UserOutlined />;
    }
  };

  const columns = [
    {
      title: t('userManagement.table.number'),
      dataIndex: 'user_id',
      key: 'user_id',
      width: 60,
      align: 'center' as const,
      render: (_: any, __: any, index: number) => users.length - index,
    },
    {
      title: t('userManagement.table.username'),
      dataIndex: 'username',
      key: 'username',
      width: 120,
      render: (username: string) => (
        <Space>
          <UserOutlined />
          <div style={{ fontWeight: '500' }}>
            {username}
          </div>
          {editingUser && editingUser.role === UserRole.ADMIN && (
            <Tooltip title={t('userManagement.table.admin')}>
              <CrownOutlined style={{ color: '#faad14' }} />
            </Tooltip>
          )}
        </Space>
      ),
    },
    {
      title: t('userManagement.table.email'),
      dataIndex: 'email',
      key: 'email',
      width: 200,
      ellipsis: true,
      render: (email: string) => (
        <Space>
          <MailOutlined />
          <div style={{ 
            maxWidth: '100%', 
            overflow: 'hidden', 
            textOverflow: 'ellipsis', 
            whiteSpace: 'nowrap' 
          }}>
            {email}
          </div>
        </Space>
      ),
    },
    {
      title: t('userManagement.table.role'),
      dataIndex: 'role',
      key: 'role',
      width: 100,
      align: 'center' as const,
      render: (role: UserRole) => (
        <Tag color={getRoleColor(role)} icon={getRoleIcon(role)}>
          {getRoleText(role)}
        </Tag>
      ),
    },
    {
      title: t('userManagement.table.organName'),
      dataIndex: 'organ_name',
      key: 'organ_name',
      width: 150,
      ellipsis: true,
      render: (organName: string) => organName || '-',
    },
    {
      title: t('userManagement.table.bubmyeng'),
      dataIndex: 'bubmyeng',
      key: 'bubmyeng',
      width: 100,
      ellipsis: true,
      render: (bubmyeng: string) => bubmyeng || '-',
    },
    {
      title: t('userManagement.table.createdAt'),
      dataIndex: 'created_at',
      key: 'created_at',
      width: 140,
      align: 'center' as const,
      render: (date: string) => (
        <span style={{ fontSize: '12px' }}>
                          {formatLocalizedDateTime(date, i18n.language)}
        </span>
      ),
    },
    {
      title: t('userManagement.table.actions'),
      key: 'actions',
      width: 120,
      align: 'center' as const,
      render: (record: User) => {
        const isCurrentUser = record.user_id === currentUser?.user_id;
        
        return (
          <Space size="small">
            <Tooltip title={t('userManagement.table.edit')}>
              <Button
                type="text"
                size="small"
                icon={<EditOutlined />}
                onClick={() => handleEdit(record)}
              />
            </Tooltip>
            <Tooltip title={t('userManagement.table.passwordChange')}>
              <Button
                type="text"
                size="small"
                icon={<KeyOutlined />}
                onClick={() => handlePasswordChange(record)}
              />
            </Tooltip>
            {!isCurrentUser && (
              <Popconfirm
                title={t('userManagement.table.deleteConfirm')}
                description={t('userManagement.table.deleteDescription')}
                onConfirm={() => handleDelete(record.user_id)}
                okText={t('userManagement.table.delete')}
                cancelText={t('common.cancel')}
              >
                <Tooltip title={t('userManagement.table.delete')}>
                  <Button
                    type="text"
                    size="small"
                    danger
                    icon={<DeleteOutlined />}
                  />
                </Tooltip>
              </Popconfirm>
            )}
          </Space>
        );
      },
    },
  ];

  return (
    <div style={{ padding: '16px' }}>
      <Card style={{ padding: '16px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <Title level={2} style={{ margin: 0 }}>
            {t('userManagement.title')}
          </Title>
          <Space>
            <span style={{ color: '#666' }}>
              {t('userManagement.totalUsers', { count: users.length })}
            </span>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={handleCreate}
            >
              {t('userManagement.create')}
            </Button>
          </Space>
        </div>

        {/* 검색 폼 */}
        <Card 
          size="small" 
          style={{ marginBottom: 16, backgroundColor: '#fafafa' }}
          styles={{ body: { padding: '16px' } }}
        >
          <Form
            form={searchForm}
            layout="inline"
            onFinish={handleSearch}
            style={{ marginBottom: 0 }}
          >
            <Row gutter={[16, 8]} style={{ width: '100%' }}>
              <Col xs={24} sm={12} md={6}>
                <Form.Item name="keyword" style={{ marginBottom: 0, width: '100%' }}>
                  <Input
                    placeholder={t('userManagement.search.keyword')}
                    prefix={<SearchOutlined />}
                    allowClear
                  />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12} md={4}>
                <Form.Item name="role" style={{ marginBottom: 0, width: '100%' }}>
                  <Select
                    placeholder={t('userManagement.search.rolePlaceholder')}
                    allowClear
                    style={{ width: '100%' }}
                  >
                    <Option value={UserRole.GENERAL}>{t('userManagement.roles.general')}</Option>
                    <Option value={UserRole.ADMIN}>{t('userManagement.roles.admin')}</Option>
                  </Select>
                </Form.Item>
              </Col>
              <Col xs={24} sm={12} md={4}>
                <Form.Item name="organ_name" style={{ marginBottom: 0, width: '100%' }}>
                  <Input
                    placeholder={t('userManagement.search.organNamePlaceholder')}
                    allowClear
                  />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12} md={4}>
                <Form.Item name="bubmyeng" style={{ marginBottom: 0, width: '100%' }}>
                  <Input
                    placeholder={t('userManagement.search.bubmyengPlaceholder')}
                    allowClear
                  />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12} md={6}>
                <Space>
                  <Button
                    type="primary"
                    htmlType="submit"
                  >
                    {t('userManagement.search.search')}
                  </Button>
                </Space>
              </Col>
            </Row>
          </Form>
        </Card>

        <Table
          columns={columns}
          dataSource={users}
          rowKey="user_id"
          loading={loading}
          pagination={{
            pageSize: 20,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) => t('pagination.showTotalUsers', { 
              start: range[0], 
              end: range[1], 
              total: total 
            }),
          }}
          scroll={{ x: 1000 }}
          size="small"
          style={{
            fontSize: '14px'
          }}
          locale={{
            emptyText: (
              <div style={{ padding: '40px 0', textAlign: 'center' }}>
                <UserOutlined style={{ fontSize: '48px', color: '#d9d9d9', marginBottom: '16px' }} />
                <div style={{ color: '#666' }}>{t('userManagement.empty.title')}</div>
              </div>
            )
          }}
        />
      </Card>

      {/* 사용자 생성/수정 모달 */}
      <Modal
        title={editingUser ? t('userManagement.modal.editTitle') : t('userManagement.modal.createTitle')}
        open={modalVisible}
        onOk={() => form.submit()}
        onCancel={() => {
          setModalVisible(false);
          setEditingUser(null);
          form.resetFields();
        }}
        okText={editingUser ? t('userManagement.modal.edit') : t('userManagement.modal.add')}
        cancelText={t('userManagement.modal.cancel')}
        width={600}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSave}
        >
          <Form.Item
            name="username"
            label={t('userManagement.modal.username')}
            rules={validationRules.username}
          >
            <Input placeholder={t('userManagement.modal.usernamePlaceholder')} disabled={!!editingUser} />
          </Form.Item>

          <Form.Item
            name="email"
            label={t('userManagement.modal.email')}
            rules={validationRules.email}
          >
            <Input placeholder={t('userManagement.modal.emailPlaceholder')} />
          </Form.Item>

          {/* 비밀번호 필드: 사용자 추가 시에만 표시 */}
          {!editingUser && (
            <Form.Item
              name="password"
              label={t('userManagement.modal.password')}
              rules={[{ required: true, min: 6, message: t('userManagement.modal.passwordRequired') }]}
            >
              <Input.Password placeholder={t('userManagement.modal.passwordPlaceholder')} />
            </Form.Item>
          )}

          <Form.Item
            name="role"
            label={t('userManagement.modal.role')}
            rules={validationRules.role}
          >
            <Select placeholder={t('userManagement.modal.rolePlaceholder')}>
              <Option value={UserRole.GENERAL}>{t('userManagement.roles.general')}</Option>
              <Option value={UserRole.ADMIN}>{t('userManagement.roles.admin')}</Option>
            </Select>
          </Form.Item>

          <Form.Item
            name="organ_name"
            label={t('userManagement.modal.organName')}
            rules={validationRules.organ_name}
          >
            <Input placeholder={t('userManagement.modal.organNamePlaceholder')} />
          </Form.Item>

          <Form.Item
            name="bubmyeng"
            label={t('userManagement.modal.bubmyeng')}
            rules={validationRules.bubmyeng}
          >
            <Input placeholder={t('userManagement.modal.bubmyengPlaceholder')} />
          </Form.Item>
        </Form>
      </Modal>

      {/* 비밀번호 변경 모달 */}
      <Modal
        title={t('userManagement.modal.passwordChangeTitle')}
        open={passwordModalVisible}
        onOk={() => passwordForm.submit()}
        onCancel={() => {
          setPasswordModalVisible(false);
          setSelectedUser(null);
          passwordForm.resetFields();
        }}
        okText={t('userManagement.modal.change')}
        cancelText={t('userManagement.modal.cancel')}
        width={400}
      >
        <Form
          form={passwordForm}
          layout="vertical"
          onFinish={handlePasswordSave}
        >
          <Form.Item
            name="password"
            label={t('userManagement.modal.password')}
            rules={validationRules.password}
          >
            <Input.Password placeholder={t('userManagement.modal.passwordPlaceholder')} />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default UserManagement; 